import React, { createContext, useState, useEffect, useContext } from "react";
import { onAuthStateChanged, signOut as firebaseSignOut } from "firebase/auth";
import { ref, get, update, remove } from "firebase/database";
import { auth, db } from "./firebase";

// Helper to normalize email for /students
function normalizeEmail(email) {
  return email.trim().toLowerCase().replace(/\./g, ',');
}

// Call this function when deleting a user
export async function removeStudentFromStudentsNode(email) {
  const emailKey = normalizeEmail(email);
  const studentRef = ref(db, `students/${emailKey}`);
  await remove(studentRef);
}

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        try {
          // Try users collection first (admin/assistant or stored profile)
          const usersRef = ref(db, "users");
          const snapshot = await get(usersRef);

          let foundRole = null;
          let foundCourse = null;

          if (snapshot.exists()) {
            const usersData = snapshot.val();
            const foundUser = Object.values(usersData).find(
              (u) => u.email === user.email
            );

            if (foundUser) {
              // Treat any presence in users node as staff unless explicitly set otherwise
              foundRole = foundUser.role || "admin";
              // If user is admin/assistant/superadmin, stop here: do NOT derive student enrollment or pending state
              if (foundRole === "admin" || foundRole === "assistant" || foundRole === "superadmin") {
                setRole(foundRole);
                setEnrolledCourse(null);
                setLoading(false);
                return; // short-circuit for non-student roles
              }
              foundCourse = foundUser.enrolledCourse || foundCourse;
            }
          }

          // If no course yet, search courses/{courseId}/students by email
          if (!foundCourse) {
            const coursesRef = ref(db, "courses");
            const coursesSnapshot = await get(coursesRef);
            if (coursesSnapshot.exists()) {
              const courses = coursesSnapshot.val();
              for (const [courseId, courseData] of Object.entries(courses)) {
                if (courseData.students) {
                  const student = Object.values(courseData.students).find(
                    (s) => s.email === user.email
                  );
                  if (student) {
                    foundCourse = courseId;
                    // If role wasn't set, consider them a student
                    if (!foundRole) foundRole = student.role || "student";
                    break;
                  }
                }
              }
            }
          }

          // If still no course found, check if user has a pending registration request
          if (!foundCourse) {
            try {
              const studentsRef = ref(db, "students");
              const studentsSnapshot = await get(studentsRef);
              if (studentsSnapshot.exists()) {
                const studentsData = studentsSnapshot.val();
                const emailKey = user.email.toLowerCase().replace(/\./g, ',');
                const studentRecord = studentsData[emailKey];

                if (studentRecord && studentRecord.status === "pending") {
                  // User has a pending registration - they should complete registration
                  foundCourse = "pending_registration";
                  if (!foundRole) foundRole = "student";
                }
              }
            } catch (error) {
              console.error("Error checking pending registration:", error);
            }
          }

          setRole(foundRole);
          setEnrolledCourse(foundCourse || null);
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUser(null);
        setRole(null);
        setEnrolledCourse(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const logout = async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        // Check if user is a student
        const coursesRef = ref(db, "courses");
        const coursesSnapshot = await get(coursesRef);

        if (coursesSnapshot.exists()) {
          const courses = coursesSnapshot.val();
          let updatePromises = [];

          // Search through all courses for the student
          for (const [courseId, courseData] of Object.entries(courses)) {
            if (courseData.students) {
              const student = Object.values(courseData.students).find(
                (s) => s.email === currentUser.email
              );
              if (student) {
                // Update isLoggedIn status to false
                const studentRef = ref(db, `courses/${courseId}/students/${student.studentId}`);
                updatePromises.push(update(studentRef, { isLoggedIn: false }));
              }
            }
          }

          // Wait for all updates to complete
          if (updatePromises.length > 0) {
            await Promise.all(updatePromises);
          }
        }
      }

      await firebaseSignOut(auth);
      setUser(null);
      setRole(null);
      setEnrolledCourse(null);
    } catch (error) {
      console.error("Logout error:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        enrolledCourse,
        loading,
        setRole,
        setEnrolledCourse,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};